import { useMemo, useState } from 'react'
import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import { Eye, FileText, FlaskConical, Pill, Stethoscope } from 'lucide-react'

import {
  buildUnifiedMedicalTimeline,
  type UnifiedMedicalTimelineItem,
  type UnifiedMedicalTimelineKind,
  type UnifiedMedicalTimelineSeverity
} from '@/lib/unified-medical-timeline'
import type {
  HealthEntry,
  LabResult,
  MedicalDocument,
  MedicationHistory
} from '@/types/health'

type TimelineFilter = 'all' | UnifiedMedicalTimelineKind

function formatTimelineDate(value: string) {
  return format(parseISO(value), 'dd MMM yyyy', { locale: fr })
}

function getKindLabel(kind: UnifiedMedicalTimelineKind) {
  switch (kind) {
    case 'document':
      return 'Document'
    case 'biology':
      return 'Biologie'
    case 'journal':
      return 'Journal'
    case 'treatment':
      return 'Traitement'
  }
}

function getKindIcon(kind: UnifiedMedicalTimelineKind) {
  switch (kind) {
    case 'document':
      return FileText
    case 'biology':
      return FlaskConical
    case 'journal':
      return Stethoscope
    case 'treatment':
      return Pill
  }
}

function getTone(severity: UnifiedMedicalTimelineSeverity) {
  if (severity === 'high') {
    return {
      card: 'border-red-500/20 bg-red-500/8',
      badge: 'border-red-500/30 bg-red-500/10 text-red-100'
    }
  }

  if (severity === 'moderate') {
    return {
      card: 'border-amber-500/20 bg-amber-500/8',
      badge: 'border-amber-500/30 bg-amber-500/10 text-amber-100'
    }
  }

  return {
    card: 'border-dark-border bg-dark-surface-high',
    badge: 'border-dark-border bg-dark-surface text-slate-300'
  }
}

function TimelineCard({
  item,
  document,
  onPreviewDocument
}: {
  item: UnifiedMedicalTimelineItem
  document: MedicalDocument | null
  onPreviewDocument?: (document: MedicalDocument) => void
}) {
  const Icon = getKindIcon(item.kind)
  const tone = getTone(item.severity)
  const canPreview = Boolean(document && onPreviewDocument)

  return (
    <article className={`rounded-2xl border p-4 ${tone.card}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-dark-border bg-dark-surface text-cyan-200">
          <Icon className="h-4 w-4" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-white">{item.title}</p>
            <span className={`rounded-full border px-2.5 py-1 text-[11px] ${tone.badge}`}>
              {getKindLabel(item.kind)}
            </span>
            {item.specialtyLabel && (
              <span className="rounded-full border border-dark-border bg-dark-surface px-2.5 py-1 text-[11px] text-slate-300">
                {item.specialtyLabel}
              </span>
            )}
          </div>

          <p className="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500">
            {formatTimelineDate(item.date)}
          </p>

          {item.subtitle && <p className="mt-2 text-sm text-slate-300">{item.subtitle}</p>}
          <p className="mt-2 text-sm text-slate-200">{item.detail}</p>
        </div>

        {canPreview && (
          <button
            type="button"
            onClick={() => onPreviewDocument?.(document!)}
            className="rounded-lg border border-dark-border bg-dark-surface p-2 text-slate-300 transition-colors hover:text-white"
            title="Apercu du PDF"
            aria-label={`Apercu ${item.title}`}
          >
            <Eye className="h-4 w-4" />
          </button>
        )}
      </div>
    </article>
  )
}

export default function UnifiedMedicalTimeline({
  documents,
  labResults,
  healthEntries,
  medicationHistory,
  onPreviewDocument,
  title = 'Timeline medicale',
  description = 'Documents, biologie, journal et traitements dans une seule chronologie.',
  limit = 18
}: {
  documents: MedicalDocument[]
  labResults: LabResult[]
  healthEntries: HealthEntry[]
  medicationHistory: MedicationHistory[]
  onPreviewDocument?: (document: MedicalDocument) => void
  title?: string
  description?: string
  limit?: number
}) {
  const [filter, setFilter] = useState<TimelineFilter>('all')

  const documentsById = useMemo(
    () => new Map(documents.map((document) => [document.id, document])),
    [documents]
  )

  const items = useMemo(
    () =>
      buildUnifiedMedicalTimeline({
        documents,
        labResults,
        healthEntries,
        medicationHistory,
        limit
      }),
    [documents, labResults, healthEntries, medicationHistory, limit]
  )

  const visibleItems = useMemo(
    () => items.filter((item) => filter === 'all' || item.kind === filter),
    [items, filter]
  )

  const filters: Array<{ id: TimelineFilter; label: string }> = [
    { id: 'all', label: 'Tout' },
    { id: 'document', label: 'Documents' },
    { id: 'biology', label: 'Biologie' },
    { id: 'journal', label: 'Journal' },
    { id: 'treatment', label: 'Traitements' }
  ]

  return (
    <div className="card space-y-4">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <p className="mt-1 text-sm text-slate-400">{description}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {filters.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setFilter(entry.id)}
              className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                filter === entry.id
                  ? 'border-cyan-500 bg-cyan-500/15 text-cyan-100'
                  : 'border-dark-border bg-dark-surface text-slate-300 hover:text-white'
              }`}
            >
              {entry.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {visibleItems.length > 0 ? (
          visibleItems.map((item) => (
            <TimelineCard
              key={item.id}
              item={item}
              document={item.previewDocumentId ? documentsById.get(item.previewDocumentId) || null : null}
              onPreviewDocument={onPreviewDocument}
            />
          ))
        ) : (
          <div className="rounded-2xl border border-dark-border bg-dark-surface-high px-4 py-6 text-sm text-slate-400">
            Aucun evenement visible pour ce filtre.
          </div>
        )}
      </div>
    </div>
  )
}
